import React, { useState, useEffect } from 'react';
import { Box, TextField, Button, Typography, Alert, Paper, CircularProgress, Table, TableBody, TableCell, TableHead, TableRow, TableContainer } from '@mui/material';
import { Print as PrintIcon } from '@mui/icons-material';
import axios from 'axios';
import baseURL from '../../../baseURL';
import { printAuctionRegister } from '../../../utils/printUtility';

const formatDate = (d) => {
  if (!d) return '';
  const x = new Date(d);
  return x.toISOString().slice(0, 10);
};

const AuctionListTab = () => {
  const [catalogueNo, setCatalogueNo] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [catalogues, setCatalogues] = useState([]);
  const [cataloguesLoading, setCataloguesLoading] = useState(false);
  const [printLoading, setPrintLoading] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    setCataloguesLoading(true);
    axios.get(`${baseURL}/auction/catalogue`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setCatalogues(res.data.data || []))
      .catch(() => setCatalogues([]))
      .finally(() => setCataloguesLoading(false));
  }, []);

  const fetchList = () => {
    if (!catalogueNo.trim()) {
      setError('Enter Catalogue Number');
      return;
    }
    setError('');
    setLoading(true);
    const token = localStorage.getItem('token');
    axios.get(`${baseURL}/auction/list`, { params: { catalogueNo: catalogueNo.trim() }, headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setData(res.data.data))
      .catch((err) => {
        setError(err.response?.data?.message || err.message);
        setData(null);
      })
      .finally(() => setLoading(false));
  };

  const handlePrintRegister = (catNo) => {
    setError('');
    setPrintLoading(catNo);
    const token = localStorage.getItem('token');
    axios.get(`${baseURL}/auction/register`, { params: { catalogueNo: catNo }, headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        if (res.data.success && res.data.data) return printAuctionRegister(res.data.data);
        throw new Error(res.data.message || 'No data');
      })
      .catch((err) => setError(err.response?.data?.message || err.message || 'Failed to load register'))
      .finally(() => setPrintLoading(null));
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>Auction List</Typography>
      {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>}

      <Typography variant="subtitle2" gutterBottom>Catalogue list – Print Auction Register</Typography>
      <TableContainer component={Paper} sx={{ maxHeight: 280, mb: 3 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Catalogue No</TableCell>
              <TableCell>Auction Date</TableCell>
              <TableCell align="right">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {cataloguesLoading ? (
              <TableRow><TableCell colSpan={3} align="center"><CircularProgress size={24} /></TableCell></TableRow>
            ) : catalogues.length === 0 ? (
              <TableRow><TableCell colSpan={3} align="center">No catalogues</TableCell></TableRow>
            ) : (
              catalogues.map((c) => (
                <TableRow key={c._id}>
                  <TableCell>{c.catalogueNo}</TableCell>
                  <TableCell>{formatDate(c.auctionDate)}</TableCell>
                  <TableCell align="right">
                    <Button size="small" variant="outlined" startIcon={<PrintIcon />} disabled={!!printLoading} onClick={() => handlePrintRegister(c.catalogueNo)}>
                      {printLoading === c.catalogueNo ? <CircularProgress size={20} /> : 'Print Auction Register'}
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>Auction list summary (by catalogue number)</Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <TextField label="Catalogue Number" value={catalogueNo} onChange={(e) => setCatalogueNo(e.target.value)} size="small" placeholder="e.g. 1/2026" />
        <Button variant="contained" onClick={fetchList} disabled={loading}>{loading ? <CircularProgress size={24} /> : 'Show'}</Button>
      </Box>
      {data && (
        <Paper sx={{ p: 2, maxWidth: 500 }}>
          <Typography variant="subtitle1"><strong>NAME OF THE AUCTIONEER:</strong> {data.auctioneerName || '–'}</Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}><strong>ADDRESS:</strong><br />{data.auctioneerAddress || '–'}</Typography>
          <Typography variant="subtitle1" sx={{ mt: 2 }}><strong>TOTAL BID AMOUNT:</strong> {data.totalBidAmount?.toFixed(2)}</Typography>
          <Typography variant="subtitle1"><strong>TOTAL AUCTION CHG:</strong> {data.totalAuctionChg?.toFixed(2)}</Typography>
        </Paper>
      )}
    </Box>
  );
};

export default AuctionListTab;
